import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  cn,
  DataTable,
  ScrollArea,
  V2Loader,
  DataTablePagination,
} from "web-design-system";
import { useTransactions } from "creso-core/hooks";
import {
  getPaginationRowModel,
  getCoreRowModel,
  useReactTable,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import type { SubmittedOrder } from "creso-core/types";
import { formatDate } from "date-fns";
import { FileTextIcon } from "lucide-react";
import { AmcLogo } from "../logos";
import { OrderTypeBadge } from "../badges";
import { Amount, Units } from "../primitives";

const columnHelper = createColumnHelper<SubmittedOrder>();

export type TransactionListDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `undefined` while the host app is still resolving the account. */
  investmentAccount: string | undefined;
  isin: string;
  source: "INTERNAL" | "EXTERNAL" | "OVERALL" | undefined;
  /** Limits the list to one folio; all folios when omitted. */
  folioId?: number | null;
  /** Host-side loading, e.g. while the client record is fetched. */
  isLoading?: boolean;
};

/** Transactions of one scheme in an investment account, paged five at a time. */
export function TransactionListDialog({
  open,
  onOpenChange,
  investmentAccount,
  isin,
  source,
  folioId,
  isLoading = false,
}: TransactionListDialogProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const { data: transactions, isLoading: isLoadingTransactions } =
    useTransactions(
      {
        investment_account_guid: investmentAccount || "",
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
        isin: isin || "",
        external: source !== "INTERNAL",
        folio_id: folioId ?? undefined,
      },
      {
        enabled: open && !!isin && !!source && !!investmentAccount,
      }
    );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("folio_number", {
        id: "scheme_name",
        header: "Folios",
        cell: (info) => {
          return <span className="text-sm">{info.getValue()}</span>;
        },
        size: 150,
      }),
      columnHelper.accessor("amount", {
        id: "transaction_amount",
        header: "Amount/Units",
        cell: (info) => {
          return (
            <div className="flex flex-col gap-1">
              {info.row.original.amount && info.row.original.units ? (
                <>
                  <span className="text-sm">
                    <Amount precision={0}>{info.row.original.amount}</Amount>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Units>{info.row.original.units}</Units>
                  </span>
                </>
              ) : (
                <span className="text-sm">
                  {info.row.original.amount ? (
                    <Amount precision={0}>{info.row.original.amount}</Amount>
                  ) : info.row.original.units ? (
                    <Units>{info.row.original.units}</Units>
                  ) : (
                    "-"
                  )}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: "Type",
        cell: (info) => {
          return <OrderTypeBadge type={info.row.original.type} />;
        },
      }),
      columnHelper.accessor("trxn_date", {
        id: "trxn_date",
        header: "Date",
        cell: (info) => {
          return (
            <div>{formatDate(info.row.original.trxn_date, "dd MMM yyyy")}</div>
          );
        },
      }),
      columnHelper.accessor("order_remark", {
        id: "order_remark",
        header: "Remarks",
        cell: (info) => {
          return <div>{info.row.original.order_remark}</div>;
        },
      }),
    ];
  }, []);

  const table = useReactTable({
    data: transactions?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: transactions?.count || 0,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    enableRowSelection: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-screen-lg flex flex-col h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="!text-2xl">Transactions</DialogTitle>
        </DialogHeader>
        <V2Loader
          className="gap-6"
          isLoading={isLoading || isLoadingTransactions}
        >
          {transactions?.results?.length ? (
            <>
              <div className="flex flex-row gap-2 items-center">
                <AmcLogo
                  amc_code={
                    transactions?.results[0]?.amc_morningstar_code as string
                  }
                />
                <span className="text-lg font-semibold">
                  {transactions?.results[0]?.scheme_name}
                </span>
                <Badge
                  variant="custom"
                  className={cn(
                    "capitalize flex-shrink-0",
                    source === "INTERNAL"
                      ? "bg-tag-slate text-tag-slate-foreground"
                      : "bg-tag-yellow text-tag-yellow-foreground"
                  )}
                >
                  {source === "INTERNAL" ? "Internal" : "External"}
                </Badge>
                <Badge
                  variant="custom"
                  className="capitalize bg-tag-slate text-tag-slate-foreground flex-shrink-0"
                >
                  {folioId
                    ? `Folio ${transactions?.results[0]?.folio_number}`
                    : "All folios"}
                </Badge>
              </div>
              <div className="flex flex-col flex-1 border border-border rounded-lg overflow-hidden">
                <ScrollArea className="flex-1">
                  <DataTable
                    pagination={false}
                    className="flex-1 border-none"
                    table={table}
                  />
                </ScrollArea>
                <DataTablePagination table={table} />
              </div>
            </>
          ) : (
            <div className="flex flex-col border border-border bg-card rounded-lg overflow-hidden gap-4 size-full items-center justify-center">
              <FileTextIcon className="size-16" strokeWidth={1} />
              No transactions found.
            </div>
          )}
        </V2Loader>
      </DialogContent>
    </Dialog>
  );
}
